import { fmtMsg } from "commonui";
import { CommonLocale, LessonPlanLocale } from "@/locales/localeid";
import {
	MethodologyType,
	LessonPlanVersionStatus,
	MethodologyTypeEnum,
	ApplicationViews,
	VcsMenuCategoryPermission,
	LessonPlanTemplateRowsType,
	MediaSlot,
	MediaType,
	MediaIconType,
	VcsMenuCategory,
	InteractionStyles,
	MediaStatus,
	CurriculumType
} from "./enum";
import { Container } from "@/components/layout";
import { ContainerType } from "@/services/admin";
import { InteractionStyleModel } from "@/services/conversation-builder";

export const EmptyGuid = "00000000-0000-0000-0000-000000000000";
export const AllLanguageSkill = -1;
export const AllLevel = -1;
export const NoneLevel = -2;
export const Unit1Id = 1;
export const TopIntentMinScore = 80;
export const NonePathValue = "None";
export const SpeechRecognitionLanguageEnglish = "en-US";
export const ZeroScore = 0.0;
export const ChildNameForConversation = "Peter";
export const TitleForConversation = "Test Conversation";

export const EstimatedTimeRowKey: string = "rowKey1";
export const TransitionRowKey: string = "rowKey2";
export const QuestionOrConversationRowKey: string = "rowKey3";
export const ConversationTitle: string = "Conversation";

export const StartDialogDefaultInteractionStyle = InteractionStyles.Neutral;
export const DialogDefaultInteractionStyle = InteractionStyles.Positive;
export const PathDefaultInteractionStyle = InteractionStyles.Positive;
export const DefaultIsConclusionDialogVal = false;

//26214400 bytes = 25mb
export const PdfFileSize = 26214400;
export const PdfFileType = "application/pdf";

export class ListStateStoreKeys {
	static ContentAdministrator = "content-admin-list-state";
	static WordManager = "word-manager-list-state";
	static RevisionManager = "revision-manager-list-state";
}

export interface AppSettingsModel {
	version: string;
	copyRightYear: string;
}

export const MethodologyTypeItems: Map<MethodologyType, string> = new Map([
	[MethodologyType.Classic, fmtMsg(CommonLocale.CommonMethodologyClassicText)],
	[MethodologyType.Digital, fmtMsg(CommonLocale.CommonMethodologyDigitalText)],
	[MethodologyType.Remote, fmtMsg(CommonLocale.CommonMethodologyRemoteText)],
	[MethodologyType.Independent, fmtMsg(CommonLocale.CommonMethodologyIndependentText)],
	[MethodologyType.REP, fmtMsg(CommonLocale.CommonMethodologyREPText)]
]);

export const MethodologyTypeItemsForTemplate: Map<MethodologyType, string> = new Map([
	[MethodologyType.Classic, fmtMsg(CommonLocale.CommonMethodologyClassicText)],
	[MethodologyType.Digital, fmtMsg(CommonLocale.CommonMethodologyDigitalText)],
	[MethodologyType.Remote, fmtMsg(CommonLocale.CommonMethodologyRemoteText)]
]);

export const MethodologyTypeItemsWithAll: Map<MethodologyTypeEnum, string> = new Map([
	[MethodologyTypeEnum.All, fmtMsg(CommonLocale.CommonMethodologyAllText)],
	[MethodologyTypeEnum.Classic, fmtMsg(CommonLocale.CommonMethodologyClassicText)],
	[MethodologyTypeEnum.Digital, fmtMsg(CommonLocale.CommonMethodologyDigitalText)],
	[MethodologyTypeEnum.Remote, fmtMsg(CommonLocale.CommonMethodologyRemoteText)],
	[MethodologyTypeEnum.Independent, fmtMsg(CommonLocale.CommonMethodologyIndependentText)],
	[MethodologyTypeEnum.REP, fmtMsg(CommonLocale.CommonMethodologyREPText)]
]);

export const LessonPlanVersionStatusMap: Map<LessonPlanVersionStatus, string> = new Map([
	[LessonPlanVersionStatus.Draft, fmtMsg(LessonPlanLocale.LessonPlanVersionStatusDraftText)],
	[LessonPlanVersionStatus.Published, fmtMsg(LessonPlanLocale.LessonPlanVersionStatusPublishedText)],
	[LessonPlanVersionStatus.Internal, fmtMsg(LessonPlanLocale.LessonPlanVersionInternalStatus)]
]);

export const LessonPlanTemplateRowsTypeStyleMap: Map<LessonPlanTemplateRowsType, string> = new Map([
	[LessonPlanTemplateRowsType.Header, "#70ad47"],
	[LessonPlanTemplateRowsType.TeachingPlan, "#5b9bd5"],
	[LessonPlanTemplateRowsType.Transition, "#ffc000"],
	[LessonPlanTemplateRowsType.None, "#fff"]
]);

export interface AppMenuItem {
	permissionId: VcsMenuCategoryPermission;
	path?: string;
	view?: ApplicationViews;
	localeId?: string;
}

export const MediaSlotTypeMap = {
	[MediaSlot.ViewingContent]: MediaType.PDF,
	[MediaSlot.AlternateContent]: MediaType.PDF,
	[MediaSlot.Audio]: MediaType.MP3,
	[MediaSlot.AudioBackground]: MediaType.PDF,
	[MediaSlot.StreamingVideo]: MediaType.MP4,
	[MediaSlot.OfflineVideo]: MediaType.MP4,
	[MediaSlot.TeacherManual]: MediaType.PDF,
	[MediaSlot.Thumbnail]: MediaType.PNG,
	[MediaSlot.Image]: MediaType.PNG,
	[MediaSlot.IndividualAudio]: MediaType.MP3
};

export const MediaSlotTypeIconTypeMap = {
	[MediaSlot.ViewingContent]: MediaIconType.Pdf,
	[MediaSlot.AlternateContent]: MediaIconType.Pdf,
	[MediaSlot.Audio]: MediaIconType.Audio,
	[MediaSlot.AudioBackground]: MediaIconType.Pdf,
	[MediaSlot.StreamingVideo]: MediaIconType.Video,
	[MediaSlot.OfflineVideo]: MediaIconType.Video,
	[MediaSlot.TeacherManual]: MediaIconType.Pdf,
	[MediaSlot.Thumbnail]: MediaIconType.Image,
	[MediaSlot.Image]: MediaIconType.Image,
	[MediaSlot.IndividualAudio]: MediaIconType.Audio
};

export const MediaSlotContainerTypeMap = {
	[MediaSlot.ViewingContent]: ContainerType.File,
	[MediaSlot.AlternateContent]: ContainerType.File,
	[MediaSlot.Audio]: ContainerType.Audio,
	[MediaSlot.AudioBackground]: ContainerType.File,
	[MediaSlot.StreamingVideo]: ContainerType.Video,
	[MediaSlot.OfflineVideo]: ContainerType.Video,
	[MediaSlot.TeacherManual]: ContainerType.File,
	[MediaSlot.Thumbnail]: ContainerType.Page,
	[MediaSlot.IndividualAudio]: ContainerType.Audio,
	[MediaSlot.Image]: ContainerType.Page
};

export const MediaStatusTypeMap = {
	[MediaStatus.Archived]: "Archived",
	[MediaStatus.Published]: "Published",
	[MediaStatus.Processing]: "Processing",
	[MediaStatus.Queued]: "Queued",
	[MediaStatus.Scheduled]: "Scheduled"
};

export const MediaSlotTypeMapForView = {
	[MediaSlot.ViewingContent]: "Viewing Content",
	[MediaSlot.AlternateContent]: "Alternate Content",
	[MediaSlot.Audio]: "Audio",
	[MediaSlot.AudioBackground]: "Audio Background",
	[MediaSlot.StreamingVideo]: "Streaming Video",
	[MediaSlot.OfflineVideo]: "Offline Video",
	[MediaSlot.TeacherManual]: "Teacher Manual",
	[MediaSlot.Thumbnail]: "Thumbnail",
	[MediaSlot.ExposureAudio]: "Exposure Audio",
	[MediaSlot.ExposureVideo]: "Exposure Video",
	[MediaSlot.IndividualAudio]: "Individual Audio",
	[MediaSlot.Image]: "Image"
};

export const MediaNameToSlotMap = {
	["Viewing Content"]: MediaSlot.ViewingContent,
	["Alternate Content"]: MediaSlot.AlternateContent,
	["Audio"]: MediaSlot.Audio,
	["Audio Background"]: MediaSlot.AudioBackground,
	["Streaming Video"]: MediaSlot.StreamingVideo,
	["Offline Video"]: MediaSlot.OfflineVideo,
	["Teacher Manual"]: MediaSlot.TeacherManual,
	["Thumbnail"]: MediaSlot.Thumbnail
};

// Since some of the permission in DB and FrontEnd are categorized differently
// So, below are required permissions for mismatched menus to be visible.
export const RequiredPermissions = {
	curriculum: [
		{
			menuId: VcsMenuCategory.CurriculumManagement,
			menuCatId: VcsMenuCategoryPermission.Components
		},
		{
			menuId: VcsMenuCategory.CurriculumManagement,
			menuCatId: VcsMenuCategoryPermission.WordManager
		}
	],
	lessonPlanManagement: [
		{
			menuId: VcsMenuCategory.LessonPlan,
			menuCatId: VcsMenuCategoryPermission.CommonEntities
		},
		{
			menuId: VcsMenuCategory.LessonPlan,
			menuCatId: VcsMenuCategoryPermission.Reports
		},
		{
			menuId: VcsMenuCategory.LessonPlan,
			menuCatId: VcsMenuCategoryPermission.LessonPlanList
		},
		{
			menuId: VcsMenuCategory.LessonPlan,
			menuCatId: VcsMenuCategoryPermission.LessonPlanTemplate
		},
		{
			menuId: VcsMenuCategory.LessonPlan,
			menuCatId: VcsMenuCategoryPermission.LessonPlanVersion
		}
	]
};

export const InteractionStyleToNameMap = {
	[InteractionStyles.Encouraging]: "Encouraging",
	[InteractionStyles.Positive]: "Positive",
	[InteractionStyles.Negative]: "Negative",
	[InteractionStyles.Neutral]: "Neutral",
	[InteractionStyles.Indicative]: "Indicative"
};

export const validStartNodeInteractionStyles: InteractionStyleModel[] = [
	{
		id: InteractionStyles.Neutral,
		name: InteractionStyleToNameMap[InteractionStyles.Neutral]
	}
];

export const validDialogInteractionStyles: InteractionStyleModel[] = [
	{
		id: InteractionStyles.Neutral,
		name: InteractionStyleToNameMap[InteractionStyles.Neutral]
	},
	{
		id: InteractionStyles.Positive,
		name: InteractionStyleToNameMap[InteractionStyles.Positive]
	},
	{
		id: InteractionStyles.Indicative,
		name: InteractionStyleToNameMap[InteractionStyles.Indicative]
	}
];

export const Methodology = {
	Classic: "Classic",
	Digital: "Digital",
	Remote: "Remote",
	Independent: "Independent",
	REP: "REP"
};

export const VerionGroupLicenseTypeToNameMap = {
	[CurriculumType.LittleSEED]: "LS",
	[CurriculumType.GrapeSEED]: "GS"
};

export const LPVStatusToNameMap = {
	[LessonPlanVersionStatus.Draft]: "Draft",
	[LessonPlanVersionStatus.Internal]: "Internal",
	[LessonPlanVersionStatus.Published]: "Published"
};

export const LPActivityValidations = {
	nameCharacterLimit: 50
};

export const AssetListValidations = {
	tagNameCharacterLimit: 100,
	assetDescriptionCharacterLimit: 50
};

export const NoneVersionGroup = "-1";

export const CLUValidations = {
	intentNameLengthLimit: 50,
	entityNameLengthLimit: 50,
	projectNameLengthLimit: 50,
	conversationNameRegex: /^[a-zA-Z0-9_.-]+$/,
	invalidIntentNameRegex: /[:$&%*()+~#/\\?]/,
	invalidEntityNameRegex: /[:$&%*()+~#/\\?]/,
	entitySynonymsMaxCount: 20000,
	projectSynonymsMaxCount: 2000000,
	allEntitiesMaxCount: 350,
	allIntentsMaxCount: 500,
	allIntentsMinCount: 1,
	allUtterancesMaxCount: 25000,
	allUtterancesMinCount: 1,
	patternedUtterancesLengthLimit: 400
};

export interface PathIntentDuplicateUtterances {
	paths: string[];
	intents: string[];
	utterances: string[];
}
export interface PathIntentUtterances {
	pathId: string;
	intent: string;
	utterances: string[];
}

export const Levels = {
	one: 1,
	two: 2,
	three: 3,
	four: 4
};

export const LevelsText = {
	one: "Level 1",
	two: "Level 2",
	three: "Level 3",
	four: "Level 4"
};

export const LevelBinding = {
	one: "level1",
	two: "level2",
	three: "level3",
	four: "level4"
};

export const CommonRegex = {
	textFormattingTagsRegex: /<\/?([biu]|em|strong)>/g,
	anchorTagRegex: /<a\b[^>]*>(.*?)<\/a>/gi
};
